import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '@/shared/context/AuthProvider';

interface EmbeddedAuthContextType {
  jwt: string | null;
  isLoading: boolean;
  error: Error | null;
  refreshJwt: () => Promise<void>;
}

const EmbeddedAuthContext = createContext<EmbeddedAuthContextType | undefined>(undefined);

interface EmbeddedAuthProviderProps {
  children: ReactNode;
}

export const EmbeddedAuthProvider: React.FC<EmbeddedAuthProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [jwt, setJwt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const hasFetched = useRef(false);

  const fetchJwt = async (retryCount = 0): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const functions = getFunctions();
      const authenticateEmbedded = httpsCallable(functions, 'authenticateUrbanMixEmbedded');
      const result = await authenticateEmbedded();
      const jwtToken = (result.data as { jwt: string }).jwt;

      setJwt(jwtToken);
    } catch (err) {
      if (retryCount < 2) {
        const delay = 1000 * Math.pow(2, retryCount); // 1s, 2s
        console.log(`Embedded auth failed, retry ${retryCount + 1}/2 in ${delay}ms`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchJwt(retryCount + 1);
      }
      const error = err instanceof Error ? err : new Error('Failed to authenticate with embedded server');
      setError(error);
      console.error('Error fetching JWT:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch when user is authenticated and we haven't fetched yet
    if (user && !hasFetched.current) {
      hasFetched.current = true;
      fetchJwt();
    }
  }, [user]);

  const refreshJwt = async () => {
    await fetchJwt();
  };

  return (
    <EmbeddedAuthContext.Provider value={{ jwt, isLoading, error, refreshJwt }}>
      {children}
    </EmbeddedAuthContext.Provider>
  );
};

export const useEmbeddedAuth = (): EmbeddedAuthContextType => {
  const context = useContext(EmbeddedAuthContext);
  if (context === undefined) {
    throw new Error('useEmbeddedAuth must be used within an EmbeddedAuthProvider');
  }
  return context;
};
